import { Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Observable, of, Subject } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthenticationService } from './authentication.service';
import { LocalStorageService } from './local-storage.service';
import { environment } from '../../../environments/environment';

@Injectable({providedIn: 'root'})
export class MsalAuthService implements OnDestroy {
  private readonly destroying$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private localStorageService: LocalStorageService,
  ) {
  }

  initialize(): void {
    // Listen to MSAL events
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this.destroying$))
      .subscribe((event: EventMessage): void => {
        this.handleMsalEvent(event);
      });

    // Listen to interaction status
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus): boolean => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe((): void => this.checkAndSetActiveAccount());
  }

  private handleMsalEvent(event: EventMessage): void {
    switch (event.eventType) {
      case EventType.LOGIN_SUCCESS:
        if (!environment.production) {
          console.log('MSAL: Login success');
        }
        this.handleLoginSuccess(event.payload as AuthenticationResult);
        break;

      case EventType.LOGIN_FAILURE:
        if (!environment.production) {
          console.error('MSAL: Login failed', event.error);
        }
        this.authenticationService.logout();
        break;

      case EventType.ACQUIRE_TOKEN_SUCCESS:
        if (!environment.production) {
          console.log('MSAL: Token acquired/refreshed successfully');
        }
        this.handleTokenRefresh(event.payload as AuthenticationResult);
        break;

      case EventType.ACQUIRE_TOKEN_FAILURE:
        if (!environment.production) {
          console.error('MSAL: Token acquisition failed', event.error);
        }
        this.handleTokenFailure();
        break;

      case EventType.LOGOUT_SUCCESS:
        if (!environment.production) {
          console.log('MSAL: Logout success');
        }
        break;

      case EventType.SSO_SILENT_SUCCESS:
        if (!environment.production) {
          console.log('MSAL: SSO silent success');
        }
        break;

      case EventType.SSO_SILENT_FAILURE:
        if (!environment.production) {
          console.log('MSAL: SSO silent failure');
        }
        break;

      default:
        if (!environment.production) {
          console.log(event);
        }
        break;
    }
  }

  private handleLoginSuccess(result: AuthenticationResult): void {
    if (!this.authenticationService.isImpersonating()) {
      // Set the active account
      if (result.account) {
        this.msalService.instance.setActiveAccount(result.account);
      }

      if (!environment.production) {
        console.log('MSAL: access token result obtained, but do we actually have the token? 1', result.accessToken !== undefined);
      }

      if (!result.accessToken) {
        return this.handleTokenFailure();
      }

      this.localStorageService.set('api-token', result.accessToken);

      // Now validate with your Laravel API and get user profile
      this.authenticationService.getUserAccount().subscribe(isValid => {
        if (isValid) {
          this.router.navigate(['/']).then((): void => {
          });
        } else {
          this.authenticationService.logout();
          this.router.navigate(['/auth/login']).then((): void => {
          });
        }
      });
    }
  }

  private handleTokenRefresh(result: AuthenticationResult): void {
    if (!this.authenticationService.isImpersonating()) {
      if (!environment.production) {
        console.log('MSAL: access token result obtained, but do we actually have the token? 2', result?.accessToken !== undefined ?? false);
      }

      if (!result?.accessToken) {
        return this.handleTokenFailure();
      }

      this.localStorageService.set('api-token', result.accessToken);

      this.authenticationService.getUserAccount().subscribe();
    }
  }

  private handleTokenFailure(): void {
    if (!this.authenticationService.isImpersonating()) {
      // Silent token acquisition failed, likely need user interaction
      // This could mean token expired and can't be refreshed silently
      if (!environment.production) {
        console.warn('MSAL: Token refresh failed, attempting interactive login');
      }

      // Try to acquire token interactively (will redirect)
      this.msalService.acquireTokenRedirect({
        scopes: ['api://' + environment.msalConfig.clientId + '/access_as_user'],
      });
    }
  }

  private checkAndSetActiveAccount(): void {
    if (!environment.production) {
      console.log('MSAL: Check and set active account, if available');
    }
    const accounts: AccountInfo[] = this.msalService.instance.getAllAccounts();

    if (accounts.length === 0) {
      if (!environment.production) {
        console.log('MSAL: No accounts detected');
      }
      return;
    }

    if (!environment.production) {
      console.log('MSAL: try to set first active account ...', this.msalService.instance.getActiveAccount(), accounts.length);
    }
    if (!this.msalService.instance.getActiveAccount() && accounts.length > 0) {
      if (!environment.production) {
        console.log('MSAL: Setting active account to first account in cache');
      }
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  acquireTokenSilent(): Observable<boolean> {
    if (!this.authenticationService.isImpersonating()) {
      if (!environment.production) {
        console.log('Auth: acquireTokenSilent start');
      }
      const account: AccountInfo = this.msalService.instance.getActiveAccount();

      if (!account) {
        return of(null);
      }

      return this.msalService.acquireTokenSilent({
        scopes: ['api://' + environment.msalConfig.clientId + '/access_as_user'],
        account: account
      }).pipe(
        switchMap((result: AuthenticationResult): Observable<boolean> => {
          if (!environment.production) {
            console.log('Auth: acquireTokenSilent in-between');
          }
          this.localStorageService.set('api-token', result.accessToken);
          return this.authenticationService.getUserAccount();
        }),
        catchError((error: any): Observable<boolean> => {
          if (!environment.production) {
            console.error('MSAL: Silent token acquisition failed', error);
          }
          // Fall back to interactive
          this.msalService.acquireTokenRedirect({scopes: ['user.read']});
          return of(false);
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
